<template>
  <FormContainer>
    <template #title>Coordonnées du représentant légal</template>

    <template #content>
      <div class="form__bloc">
        <WrapperToggleSwitch
          id="field_55.5"
          v-model="estMemeAddresseRepresentantLegal"
          label="L'adresse du représentant légal est-elle la même que celle de l'apprenti ?"
          :items="OuiOuNon"
          :readonly
          :disabled="representantLegalDisabled"
          class="col-10 col-md-14 col-sm-36"
        />
      </div>

      <div class="form__bloc">
        <template v-if="estMemeAddresseRepresentantLegal">
          <WrapperInput
            id="field_47"
            :model-value="model?.apprenti?.adresse?.voie"
            :label="LABEL_NUMERO_VOIE"
            class="col-12 col-md-18 col-sm-36"
            placeholder="Ex : 10 impasse GRANET"
            disabled
          />
          <WrapperInput
            id="field_47"
            :model-value="model?.apprenti?.adresse?.complement"
            label="Complément d'adresse"
            class="col-12 col-md-18 col-sm-36"
            placeholder="Ex : Bâtiment B"
            disabled
          />
          <WrapperInput
            id="field_48"
            :model-value="model?.apprenti?.adresse?.codePostal"
            label="Code postal"
            class="col-4 col-md-6 col-sm-36"
            placeholder="Ex : 75001"
            disabled
          />
          <WrapperInput
            id="field_49"
            :model-value="model?.apprenti?.adresse?.commune"
            label="Commune"
            class="col-8 col-md-12 col-sm-36"
            placeholder="Ex : Paris"
            disabled
          />
        </template>
        <template v-else>
          <WrapperInput
            id="field_57"
            v-model="voieModel"
            :label="LABEL_NUMERO_VOIE"
            class="col-12 col-md-18 col-sm-36"
            placeholder="Ex : 10 impasse GRANET"
            :required="meta.representantLegal?.adresse?.voie?.$status === PropertyStatus.Required"
            :alertLabel="meta.representantLegal?.adresse?.voie?.$messageErreurDirty!"
            :maxlength="38"
            deletable
            :readonly
            :disabled="representantLegalDisabled"
          />
          <WrapperInput
            id="field_57"
            v-model="complementAdresseRepresentantModel"
            label="Complément d'adresse"
            class="col-12 col-md-18 col-sm-36"
            placeholder="Ex : Bâtiment B"
            :required="meta.representantLegal?.adresse?.complement?.$status === PropertyStatus.Required"
            :alertLabel="meta.representantLegal?.adresse?.complement?.$messageErreurDirty!"
            :maxlength="38"
            deletable
            :readonly
            :disabled="representantLegalDisabled"
          />
          <WrapperAutocomplete
            id="field_58"
            key-id="codeInsee"
            key-label="intitule"
            :search="searchCommune"
            v-model="codePostalModel"
            :get-item-label="getCpCommuneLabel"
            label="Code postal"
            :search-min-length="3"
            :placeholder="PLACEHOLDER_ADR_CP"
            :readonly
            deletable
            class="col-4 col-md-6 col-sm-36"
            :strict="false"
            :required="meta.representantLegal?.adresse?.codePostal?.$status === PropertyStatus.Required"
            :alertLabel="meta.representantLegal?.adresse?.codePostal?.$messageErreurDirty!"
            :maxlength="5"
            :disabled="representantLegalDisabled"
          />
          <WrapperAutocomplete
            id="field_59"
            key-id="codeInsee"
            key-label="intitule"
            :search="searchCommune"
            v-model="communeModel"
            :get-item-label="getCpCommuneLabel"
            label="Commune"
            :search-min-length="3"
            :placeholder="PLACEHOLDER_ADR_VILLE"
            :readonly
            deletable
            class="col-8 col-md-12 col-sm-36"
            :strict="false"
            :required="meta.representantLegal?.adresse?.commune?.$status === PropertyStatus.Required"
            :alertLabel="meta.representantLegal?.adresse?.commune?.$messageErreurDirty!"
            :maxlength="50"
            :disabled="representantLegalDisabled"
          />
        </template>
      </div>
      <div class="form__bloc">
        <WrapperInputTelephone
          id="field_50"
          v-model="telRepresentantModel"
          label="Téléphone"
          :required="meta.representantLegal?.representant?.telephone?.$status === PropertyStatus.Required"
          :alertLabel="meta.representantLegal?.representant?.telephone?.$messageErreurDirty!"
          class="col-6 col-md-12 col-sm-36"
          :placeholder="PLACEHOLDER_TEL"
          deletable
          :readonly
          :disabled="representantLegalDisabled"
        />
        <WrapperInput
          id="field_60"
          v-model="emailRepresentantModel"
          label="Email"
          hint="64 caractères maximum"
          class="col-12 col-md-18 col-sm-36"
          placeholder="nom.prenom@email.fr"
          :required="meta.representantLegal?.representant?.email?.$status === PropertyStatus.Required"
          :alertLabel="meta.representantLegal?.representant?.email?.$messageErreurDirty!"
          :maxlength="64"
          deletable
          :readonly
          :disabled="representantLegalDisabled"
        />
      </div>
    </template>
  </FormContainer>
</template>

<script setup lang="ts">
  import { inject } from 'vue'
  import { storeToRefs } from 'pinia'
  import { useDemandeCAPPStore } from '@/features/interface/demande/capp/useDemandeCAPPStore'
  import { CAPP_ACTIONS } from '@/features/interface/demande/capp/useDemandeCAPPActions'
  import { OuiOuNon } from '@/features/interface/shared/tools'
  import { PropertyStatus } from '@/features/domaine/property-status/PropertyStatus'
  import {
    FormContainer,
    LABEL_NUMERO_VOIE,
    PLACEHOLDER_TEL,
    PLACEHOLDER_ADR_CP,
    PLACEHOLDER_ADR_VILLE,
  } from '@/features/interface/demande/shared'
  import { useCodePostalCommune } from '@/features/interface/composables/useCodePostalCommune'
  import type { AdresseDTO } from '@/features/api/extranet/dto/shared'
  import type { RepresentantLegalDTO } from '@/features/api/extranet/dto/Demande/CAPP'

  const store = useDemandeCAPPStore()
  const { setupFieldWithKey, setupCustomUpdater } = store
  const { meta, model, readonly } = storeToRefs(store)

  const { estMemeAddresseRepresentantLegal, representantLegalDisabled, searchCommune } = inject(CAPP_ACTIONS)!

  const { codePostalModel, communeModel, getCpCommuneLabel } = useCodePostalCommune({
    getCodePostal: () => model?.value?.representantLegal?.adresse?.codePostal,
    getCommune: () => model?.value?.representantLegal?.adresse?.commune,
    updateValues: setupCustomUpdater((patch, value) => {
      if (!patch.representantLegal) {
        patch.representantLegal = {} as RepresentantLegalDTO
      }
      const representantLegal = patch.representantLegal
      if (!representantLegal.adresse) {
        representantLegal.adresse = {} as AdresseDTO
      }
      representantLegal.adresse.codePostal = value.code ?? representantLegal.adresse.codePostal
      representantLegal.adresse.commune = value.commune ?? representantLegal.adresse.commune
    }),
  })

  const voieModel = setupFieldWithKey<string>('representantLegal.adresse.voie', true)
  const complementAdresseRepresentantModel = setupFieldWithKey<string>('representantLegal.adresse.complement', true)
  const telRepresentantModel = setupFieldWithKey<string>('representantLegal.representant.telephone', true)
  const emailRepresentantModel = setupFieldWithKey<string>('representantLegal.representant.email', true)
</script>
